/** @module Camunda/TaskInstanceReport */
define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"underscore",
	"csi/core/Key",
	"csi/def/ViewRef",
	"csi/locale",
	"csi/session",
	"csi/utility/routerUtility",
	"csi/table/Widget",
	"csi/task/util",
	"csi/view/ToolbarPluginButton",
	"csi/view/ToolbarMessage",
	"csi/xml",
	"csi/xml/Query",
	"./main",
	"./_ToolbarSelectMixin"
], function(declare, lang, _, Key, ViewRef, locale, session, routerUtility, Widget, TaskUtil, ToolbarPluginButton, ToolbarMessage, xml, Query, Camunda, _ToolbarSelectMixin) {

	const {LOCATION_FILTER} = routerUtility;

	const FILTER_ALL = "TaskInstancePlural";
	const FILTER_HASISSUES = "CamundaStateHasIssues";
	const FILTER_RUNNING = Camunda.STATE_STARTED;
	const FILTER_COMPLETE = Camunda.STATE_COMPLETED;
	const FILTER_CANCELLED = Camunda.STATE_CANCELLED;
	const FILTER_SUSPENDED = Camunda.STATE_SUSPENDED;
	const FILTER_ERROR = Camunda.STATE_ERROR;
	const LOCATION_ALL = "All";
	const LOCATION_ISSUES = "Issues";
	const LOCATION_RUNNING = "Running";
	const LOCATION_COMPLETE = "Complete";
	const LOCATION_CANCELLED = "Cancelled";
	const LOCATION_SUSPENDED = "Suspended";
	const LOCATION_ERROR = "Error";


	return declare([Widget, _ToolbarSelectMixin], {
		enableMassSelect: true,
		camundaFilterValues: [FILTER_ALL, FILTER_HASISSUES, FILTER_RUNNING, FILTER_COMPLETE, FILTER_CANCELLED, FILTER_SUSPENDED, FILTER_ERROR],
		camundaLocationFilterValue: [LOCATION_ALL, LOCATION_ISSUES, LOCATION_RUNNING, LOCATION_COMPLETE, LOCATION_CANCELLED, LOCATION_SUSPENDED, LOCATION_ERROR],

		/** @property {number} nSelectedTasks - the total count of selected tasks */
		nSelectedTasks: 0,

		/**
		 * @override
		 * Set up the toolbar actions and message
		 */
		postMixInProperties: function() {
			const defView = this.defView;
			const self = this;
			this.checkFilterParam();
			session.reserveLocationKey(LOCATION_FILTER, lang.hitch(this, this._onLocationChange));

			this.inherited(arguments);

			// set up the function to determine whether the row selectable or not
			const fd = this.defView.getFormData();

			fd.m_fAggregateTest = function(ctx) {
				return TaskUtil.isTaskActive(ctx.getNode());
			};

			// toolbar message
			this.plugins.push(defView._addViewAction("LabelInfo", {
				classToolbarPlugin: ToolbarMessage,
				classToolbarPluginParams: {
					canShowPlugin: function() {
						const total = this.getView().nSelectedTasks;
						this.set("message", locale.resource("TaskInstancesSelected", [total]));
						return !!total;
					}
				}
			}));

			if (TaskUtil.isWorkflowAdmin(this.getContext())) {
				// setup up the toolbar actions, if the user has the right to do that
				this.plugins.push(defView._addViewAction("ReRunAssignment", {
					classToolbarPlugin: ToolbarPluginButton,
					classToolbarPluginParams: {
						label: locale.resource("ReRunAssignment"),
						canShowPlugin: self._canShowAction
					},
					m_class: "csi-float-right",
					m_onAction: "_onActionReRunAssignment",
					m_postAction: "postActionRefresh"
				}));

				this.plugins.push(defView._addViewAction("AssignToUser", {
					classToolbarPlugin: ToolbarPluginButton,
					classToolbarPluginParams: {
						label: locale.resource("AssignToUser"),
						canShowPlugin: self._canShowAction
					},
					m_class: "csi-float-right",
					m_onAction: "_onActionProcessAssignee",
					m_postAction: "postActionRefresh",
					m_preAction: "_preActionPassFormdata",
					m_viewRef: lang.mixin(new ViewRef(), {
						m_id: "ActiveUsers",
						m_url: "centric://REFLECTION/INSTANCE/_CS_Data/null"
					})
				}));
			}
		},

		/**
		 * Decide whether to display the button or not
		 * @return {boolean}
		 */
		_canShowAction: function() {
			const total = this.getView().nSelectedTasks;
			return !!total;
		},

		/**
		 * Switch the select mode to single mode
		 * @param {Controller} ac
		 */
		_preActionPassFormdata: function(ac) {
			ac.getFormData().m_bSelectOne = true;
			return true;
		},

		/**
		 * Call server operation to reassign
		 * @param {Controller} ac
		 */
		_onActionReRunAssignment: function(ac) {
			var selectedTaskURLs = this.defView.getFormData().getSelectedValue();

			ac.m_writer.callList("Camunda", "RefreshAssigneeAndCandidates", selectedTaskURLs, xml.PROP_URL);
		},

		/**
		 * Change the assignee
		 * @param {Controller} ac
		 */
		_onActionProcessAssignee: function(ac) {
			const selectedTaskURLs = this.defView.getFormData().getSelectedValue();
			const assigneeURL = ac.getFormData().getSelectedValue();

			ac.m_writer.callList("Camunda", "SetAssignee", selectedTaskURLs, xml.PROP_URL_PARENT)
				.addParameter(xml.PROP_URL, xml.ATTR_REF, assigneeURL);

			this.defView.getFormData().resetSelections(this.getContext(), true);
		},


		/**
		 * @override
		 * Update location parameter
		 */
		_onActionProcessFilter: function(ac) {
			this.inherited(arguments);

			const {actionValue} = ac;
			const {camundaFilterValues, camundaLocationFilterValue} = this;
			const index = camundaFilterValues.indexOf(actionValue);
			const keyValue = camundaLocationFilterValue[index];

			session.setLocationActual(LOCATION_FILTER, keyValue, null, lang.hitch(this, this._onLocationChange));
		},

		/**
		 * @override
		 * This view's url parameters were changed while tab was active, process changes
		 */
		_onLocationChange: function(value) {
			if (this._isFilterChanged()) {
				var value = this.camundaFilterValue;
				var p = this.toolbarNode.getPlugin("CamundaInstanceFilter");
				p.set("value", value);
			}
		},

		/**
		 * @override
		 * Handle the selection of task, switch the toolbar as needed
		 */
		onSelect: function(ctx, url, bOn, bReplaced) {
			const fd = this.defView.m_formData;
			const tasks = fd.getSelectedNodes();

			this.nSelectedTasks = tasks.length;

			if (this.nSelectedTasks) {
				this.toolbarNode.pluginsMask = [
					"LabelInfo",
					"ReRunAssignment",
					"AssignToUser"
				];
			} else {
				// reset to null to display all in terms of the mask
				this.toolbarNode.pluginsMask = null;
			}

			this.inherited(arguments);
		},

		/**
		 * @override
		 * Check for url "Filter" parameter value change and apply the change
		 */
		onTabChanging(locationState) {
			if (this._isFilterChanged()) {
				const {camundaFilterValue, camundaFilterValues, camundaLocationFilterValue, toolbarNode} = this;
				const locationItem = _.find(locationState, (l) => l.key == LOCATION_FILTER);
				if (locationItem) {
					const index = camundaFilterValues.indexOf(camundaFilterValue);
					locationItem.value = camundaLocationFilterValue[index];
					toolbarNode.getPlugin("CamundaInstanceFilter").set("value", camundaFilterValue);
				}
			}
		},

		/**
		 * Check if this.camundaFilterValue differ from url Filter value
		 * @return {boolean}
		 */
		_isFilterChanged: function() {
			var oldCamundaFilterValue = this.camundaFilterValue;
			this.checkFilterParam();
			var newCamundaFilterValue = this.camundaFilterValue;
			return oldCamundaFilterValue != newCamundaFilterValue;
		},

		/**
		 * Check url parameter "Filter" and apply it's value
		 */
		checkFilterParam: function() {
			var filterValue = session.getLocationValue(LOCATION_FILTER);
			var index = this.camundaLocationFilterValue.indexOf(filterValue);
			if (index >= 0) {
				this.camundaFilterValue = this.camundaFilterValues[index];
			}
		},

		/**
		 * @override
		 * Add filter parameters
		 */
		onCreateMainQueryLevel: function(ql) {
			const value = this.camundaFilterValue;
			var hasIssue;
			var bFilterAll;
			var state;
			var query;

			if (value == FILTER_RUNNING) {
				bFilterAll = false;
				hasIssue = false;
				state = Camunda.STATE_STARTED;
			} else if (value == FILTER_COMPLETE) {
				hasIssue = false;
				bFilterAll = false;
				state = Camunda.STATE_COMPLETED;
			} else if (value == FILTER_CANCELLED) {
				bFilterAll = false;
				state = Camunda.STATE_CANCELLED;
			} else if (value == FILTER_SUSPENDED) {
				bFilterAll = false;
				state = Camunda.STATE_SUSPENDED;
			}else if (value == FILTER_ERROR) {
				bFilterAll = false;
				state = Camunda.STATE_ERROR;
			} else if (value == FILTER_HASISSUES) {
				hasIssue = true;
				bFilterAll = false;
			} else {
				bFilterAll = true;
			}

			if (!bFilterAll) {
				query = new Query();

				if (hasIssue != undefined) {
					query.Attr("HasIssues", xml.OP_EQUALS, hasIssue);
				}

				if (hasIssue) {
					query.Attr("ActivityState", xml.OP_NOT_EQUALS, Camunda.STATE_CANCELLED)
						.Attr("ActivityState", xml.OP_NOT_EQUALS, Camunda.STATE_ERROR);
				} else {
					query.Attr("ActivityState", xml.OP_EQUALS, state);
				}

				ql.serverQuery = query;
			}
		},

		/**
		 * @override
		 * Apply different Heading based on the filter
		 */
		onViewResolveKeysToSkip(skipSet) {
			const skip = (id) => {
				skipSet.add(Key.untypedKeyFromParts({id}));
			};

			switch (this.camundaFilterValue) {
				case FILTER_ALL:
					this.set("bShowActionColumn", true);
					break;
				case FILTER_HASISSUES:
				case FILTER_RUNNING:
					skip("Comments");
					skip("EndDate");
					skip("UserResponse");
					this.set("bShowActionColumn", true);
					break;
				case FILTER_CANCELLED:
					skip("Comments");
					skip("EndDate");
					skip("UserResponse");
					this.set("bShowActionColumn", false);
					break;
				case FILTER_COMPLETE:
					skip("CandidateUsers");
					this.set("bShowActionColumn", false);
					break;
			}
		}
	});
});
